import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { FadeIn } from "@/components/Reveal";

const INITIAL = { full_name: "", business_name: "", email: "", phone: "", needs: "", consent: false };

/**
 * Shared interest-list form for TSM4 services that are not yet operational
 * (Drug Testing / Fingerprinting). Submissions land in the admin portal.
 */
export const InterestForm = ({ interestType, heading, intro, submitLabel, testidPrefix }) => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setErrorMsg("Please check the consent box so TSM Group CLE can contact you about this service.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tsm4/interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, interest_type: interestType }),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
    } catch (err) {
      setErrorMsg("Something went wrong while submitting your request. Please try again.");
      setStatus("error");
    }
  };

  return (
    <section className="bg-white py-24 lg:py-32">
      <div className="container-tsm grid gap-14 lg:grid-cols-12 lg:gap-10">
        <FadeIn className="lg:col-span-5">
          <p className="eyebrow">Join the Interest List</p>
          <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl" data-testid={`${testidPrefix}-form-headline`}>
            {heading}
          </h2>
          <p className="mt-8 max-w-md leading-relaxed text-ink/70" data-testid={`${testidPrefix}-form-intro`}>
            {intro}
          </p>
          <p className="mt-8 max-w-md text-xs italic leading-relaxed text-ink/50">
            We&rsquo;ll use your information only to contact you about this service. You can unsubscribe at any time,
            and we never sell your information.
          </p>
        </FadeIn>

        <FadeIn delay={0.15} className="lg:col-span-7">
          <div className="rounded-xl border border-navy/10 bg-paper p-7 shadow-[0_8px_30px_rgb(0,0,0,0.05)] sm:p-9 lg:p-11">
            {status === "success" ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center text-center" data-testid={`${testidPrefix}-success`}>
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-teal/40 bg-teal/10">
                  <CheckCircle2 className="h-8 w-8 text-teal" />
                </span>
                <h3 className="mt-7 text-3xl font-extrabold text-navy">You&rsquo;re on the list.</h3>
                <p className="mt-4 max-w-md leading-relaxed text-ink/70">
                  Thank you. We&rsquo;ve saved your information and will contact you as soon as this service becomes
                  available.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid={`${testidPrefix}-form`}>
                <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
                  <div>
                    <Label htmlFor={`${testidPrefix}-name`} className="field-label">Contact Name *</Label>
                    <Input id={`${testidPrefix}-name`} required value={form.full_name} onChange={update("full_name")} className="field-input" placeholder="Jane Doe" data-testid={`${testidPrefix}-input-name`} />
                  </div>
                  <div>
                    <Label htmlFor={`${testidPrefix}-business`} className="field-label">Business Name</Label>
                    <Input id={`${testidPrefix}-business`} value={form.business_name} onChange={update("business_name")} className="field-input" placeholder="Your Business LLC" data-testid={`${testidPrefix}-input-business`} />
                  </div>
                  <div>
                    <Label htmlFor={`${testidPrefix}-email`} className="field-label">Email *</Label>
                    <Input id={`${testidPrefix}-email`} type="email" required value={form.email} onChange={update("email")} className="field-input" placeholder="name@company.com" data-testid={`${testidPrefix}-input-email`} />
                  </div>
                  <div>
                    <Label htmlFor={`${testidPrefix}-phone`} className="field-label">Phone *</Label>
                    <Input id={`${testidPrefix}-phone`} type="tel" required value={form.phone} onChange={update("phone")} className="field-input" placeholder="(216) 555-0100" data-testid={`${testidPrefix}-input-phone`} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor={`${testidPrefix}-needs`} className="field-label">Anticipated Needs</Label>
                    <Textarea id={`${testidPrefix}-needs`} value={form.needs} onChange={update("needs")} className="field-input min-h-24" placeholder="Tell us briefly what you expect to need — volume, timing, locations, and anything else helpful" data-testid={`${testidPrefix}-input-needs`} />
                  </div>
                  <div className="sm:col-span-2">
                    <div className="flex items-start gap-3 rounded-sm border border-navy/10 bg-white p-4">
                      <Checkbox
                        id={`${testidPrefix}-consent`}
                        checked={form.consent}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, consent: v === true }))}
                        className="mt-0.5 border-navy/30 data-[state=checked]:border-teal data-[state=checked]:bg-teal data-[state=checked]:text-white"
                        data-testid={`${testidPrefix}-input-consent`}
                      />
                      <Label htmlFor={`${testidPrefix}-consent`} className="text-sm font-normal leading-relaxed text-ink/75">
                        I agree to allow TSM Group CLE to contact me about updates on this service. *
                      </Label>
                    </div>
                  </div>
                  {status === "error" && errorMsg && (
                    <p className="sm:col-span-2 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid={`${testidPrefix}-error`}>
                      {errorMsg}
                    </p>
                  )}
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="btn-gold w-full disabled:cursor-not-allowed disabled:opacity-60"
                      data-testid={`${testidPrefix}-submit-button`}
                    >
                      {status === "submitting" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Submitting…
                        </>
                      ) : (
                        <>
                          {submitLabel}
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};
